import * as THREE from "three";
import {
	BENCH_FRAME_HEIGHT_MM,
	BENCH_FRAME_THICKNESS_MM,
	BENCH_HEATER_CLIP_CLEARANCE_MM,
	BENCH_LEDGER_HEIGHT_MM,
	BENCH_LEG_ACROSS_MM,
	BENCH_LEG_ALONG_MM,
	BENCH_SLAT_GAP_MM,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
	benchJoistCount,
	benchLegCount,
	MIN_BENCH_RUN_LENGTH_MM,
} from "../domain/interior/constants";
import { benchFrameDimensions } from "../domain/interior/benchFrame";
import type { BenchSkirt } from "../domain/interior/benchSkirt";
import { addBox, addCylinder } from "./geometry";
import {
	matVentExhaustFace,
	matVentSupplyFace,
	matVentThroat,
} from "./materials";

type Orientation = "x" | "z";

// ─── Ventilation hardware (tallriksventil / plate valve) ─────

/** Which airflow direction a vent serves — picks the neutral face material. */
export type VentKind = "supply" | "exhaust";

/** Outward normal of the wall the vent is mounted on. */
export type VentFacing = "+x" | "-x" | "+z" | "-z";

const VENT_FLANGE_R = 0.075;
const VENT_FLANGE_T = 0.01;
const VENT_THROAT_R = 0.052;
const VENT_THROAT_D = 0.02;
const VENT_PLATE_R = 0.068;
const VENT_PLATE_T = 0.014;

/** How far a plate valve stands proud of the surface it is mounted on. */
export const VENT_PROTRUSION_M = VENT_FLANGE_T + VENT_THROAT_D + VENT_PLATE_T;

/** Overall face diameter — the footprint a vent occupies on the wall. */
export const VENT_FACE_DIAMETER_M = VENT_FLANGE_R * 2;

const VENT_FACING_ROTATION: Record<VentFacing, [number, number, number]> = {
	// Parts are built along local +Y; rotate the assembly so +Y becomes the
	// wall's outward normal.
	"+z": [Math.PI / 2, 0, 0],
	"-z": [-Math.PI / 2, 0, 0],
	"+x": [0, 0, -Math.PI / 2],
	"-x": [0, 0, Math.PI / 2],
};

/**
 * Add a tallriksventil — the round plate valve every Swedish sauna uses for
 * supply and exhaust air: a flange ring flush to the surface, a dark shadow
 * gap, and the adjustable valve plate standing proud of it.
 *
 * `position` is the point ON the mounted surface (cladding face for exterior
 * vents, inner panel face for interior ones); the assembly grows outward
 * along `facing` from there.
 *
 * The face material is the NEUTRAL hardware one. The blue/red supply/exhaust
 * coding is applied by the view layer via `applyVentilationDiagramMode` —
 * see the ventilation section of `materials.ts`.
 *
 * Returns the assembly group, named `detail/vent/${name}` so the existing
 * `detail/vent/` audit prefix and BOM mapping keep matching.
 */
export function addWallVent({
	group,
	position,
	facing,
	kind,
	name,
}: {
	group: THREE.Group;
	position: THREE.Vector3;
	facing: VentFacing;
	kind: VentKind;
	name: string;
}): THREE.Group {
	const assembly = new THREE.Group();
	assembly.name = `detail/vent/${name}`;
	assembly.position.copy(position);
	const [rx, ry, rz] = VENT_FACING_ROTATION[facing];
	assembly.rotation.set(rx, ry, rz);
	group.add(assembly);

	const faceMaterial =
		kind === "supply" ? matVentSupplyFace : matVentExhaustFace;

	addCylinder(
		VENT_FLANGE_R,
		VENT_FLANGE_T,
		faceMaterial,
		0,
		VENT_FLANGE_T / 2,
		0,
		assembly,
	).name = `detail/vent/${name}/flange`;

	addCylinder(
		VENT_THROAT_R,
		VENT_THROAT_D,
		matVentThroat,
		0,
		VENT_FLANGE_T + VENT_THROAT_D / 2,
		0,
		assembly,
	).name = `detail/vent/${name}/throat`;

	addCylinder(
		VENT_PLATE_R,
		VENT_PLATE_T,
		faceMaterial,
		0,
		VENT_FLANGE_T + VENT_THROAT_D + VENT_PLATE_T / 2,
		0,
		assembly,
	).name = `detail/vent/${name}/plate`;

	return assembly;
}

export function addOpeningTrimFrame({
	group,
	width,
	height,
	depth,
	trimWidth,
	includeBottom = true,
	material,
	horizontalMaterial,
	position,
	orientation,
	namePrefix,
	dripCap = false,
	sill = false,
	dripCapMaterial,
	sillMaterial,
	outwardSign = 1,
	axisSpan,
}: {
	group: THREE.Group;
	width: number;
	height: number;
	depth: number;
	trimWidth: number;
	includeBottom?: boolean;
	material: THREE.Material;
	/**
	 * Material for the LYING boards of the frame (head, sill-side bottom
	 * board, drip cap, sill). Wood-textured trim materials have their
	 * grain running along vertical boards; a horizontal board needs the
	 * 90°-rotated variant or it reads as a strip of panel. Falls back to
	 * `material` when omitted.
	 */
	horizontalMaterial?: THREE.Material;
	position: THREE.Vector3;
	orientation: Orientation;
	namePrefix?: string;
	/**
	 * Add a Swedish "vattenbleck" drip cap — a thin overhanging
	 * board above the opening that sticks out 25 mm past the trim
	 * to shed water clear of the wall. Doors and windows both have
	 * these on real outdoor saunas; without them rain runs straight
	 * down the cladding behind the foder.
	 */
	dripCap?: boolean;
	/**
	 * Add a sloped exterior window sill below the opening — a
	 * 90 mm deep board angled ~8° downward, projecting past the
	 * cladding so water sheds clear. Windows only; doors sit on
	 * the threshold.
	 */
	sill?: boolean;
	dripCapMaterial?: THREE.Material;
	sillMaterial?: THREE.Material;
	/**
	 * Sign of the outward facade normal along the opening's
	 * orientation axis. +1 for front (z+) / right (x+); -1 for
	 * back (z−) / left (x−). The base trim is symmetric so this
	 * only matters for the optional drip cap and sill, which must
	 * project AWAY from the building and slope DOWNWARD outward
	 * — otherwise water sheds back into the wall on half the
	 * facades.
	 */
	outwardSign?: 1 | -1;
	/**
	 * Clamp for the frame's extent along its WIDTH axis, relative to
	 * the opening centre. A full-wall panorama opening otherwise draws
	 * jambs, head, drip cap and sill straight through the knutbrädor at
	 * both wall ends. A jamb leg that no longer fits inside the span is
	 * dropped entirely — the corner board takes its place — and the
	 * horizontal boards stop at the clamp instead of overhanging it.
	 */
	axisSpan?: { startM?: number; endM?: number };
}) {
	// Side pieces extend by trimWidth past every edge that has a trim
	// board (top/bottom). When `includeBottom` is false (e.g. a door
	// sitting on the threshold), the side trim only extends past the
	// top, and its bottom is lifted by ~12 mm so it clears the wall's
	// base trim (which sits ~10 mm above the wall base). Without that
	// lift the side trim would AABB-collide with the base trim and
	// either look wrong or trigger structural integrity tests.
	const BASE_TRIM_CLEARANCE = 0.012;
	const sideTopExtension = trimWidth;
	const sideBottomLift = includeBottom ? -trimWidth : BASE_TRIM_CLEARANCE;
	const sideHeight = height + sideTopExtension - sideBottomLift;
	const sideCenterY = position.y + (sideTopExtension + sideBottomLift) / 2;

	// Drip-cap / sill dimensions — match Swedish standard 22×120 mm
	// vattenbleck and ~45×95 mm sill. Both project past the side trim
	// so water always falls clear of the cladding underneath.
	const dripCapHeight = 0.022;
	const dripCapDepth = depth + 0.025;
	const sillThickness = 0.04;
	const sillDepth = depth + 0.045;
	const lyingMaterial = horizontalMaterial ?? material;
	const trimMatForExtras = dripCapMaterial ?? lyingMaterial;
	const sillMatResolved = sillMaterial ?? lyingMaterial;

	// Everything below is expressed on the frame's width axis relative to
	// the opening centre, clamped to axisSpan (see the param doc).
	const halfW = width / 2;
	const spanStart = axisSpan?.startM ?? Number.NEGATIVE_INFINITY;
	const spanEnd = axisSpan?.endM ?? Number.POSITIVE_INFINITY;
	const startLegFits = -halfW - trimWidth >= spanStart - 1e-6;
	const endLegFits = halfW + trimWidth <= spanEnd + 1e-6;
	const boardStart = Math.max(-halfW, spanStart);
	const boardEnd = Math.min(halfW, spanEnd);
	const boardSpan = boardEnd - boardStart;
	const boardCenter = (boardStart + boardEnd) / 2;
	const dripStart = startLegFits ? boardStart - trimWidth - 0.03 : boardStart;
	const dripEnd = endLegFits ? boardEnd + trimWidth + 0.03 : boardEnd;
	const dripCapWidth = dripEnd - dripStart;
	const dripCenter = (dripStart + dripEnd) / 2;
	const sillStart = startLegFits ? boardStart - trimWidth - 0.02 : boardStart;
	const sillEnd = endLegFits ? boardEnd + trimWidth + 0.02 : boardEnd;
	const sillWidth = sillEnd - sillStart;
	const sillCenter = (sillStart + sillEnd) / 2;

	if (orientation === "z") {
		if (startLegFits) {
			const left = addBox(
				trimWidth,
				sideHeight,
				depth,
				material,
				position.x + boardStart - trimWidth / 2,
				sideCenterY,
				position.z,
				group,
			);
			if (namePrefix) {
				left.name = `detail/opening-trim/${namePrefix}-left`;
			}
		}
		if (endLegFits) {
			const right = addBox(
				trimWidth,
				sideHeight,
				depth,
				material,
				position.x + boardEnd + trimWidth / 2,
				sideCenterY,
				position.z,
				group,
			);
			if (namePrefix) {
				right.name = `detail/opening-trim/${namePrefix}-right`;
			}
		}
		const top = addBox(
			boardSpan,
			trimWidth,
			depth,
			lyingMaterial,
			position.x + boardCenter,
			position.y + height / 2 + trimWidth / 2,
			position.z,
			group,
		);
		if (namePrefix) {
			top.name = `detail/opening-trim/${namePrefix}-top`;
		}
		if (includeBottom) {
			const bottom = addBox(
				boardSpan,
				trimWidth,
				depth,
				lyingMaterial,
				position.x + boardCenter,
				position.y - height / 2 - trimWidth / 2,
				position.z,
				group,
			);
			if (namePrefix) {
				bottom.name = `detail/opening-trim/${namePrefix}-bottom`;
			}
		}
		if (dripCap) {
			// Drip cap sits ON TOP of the upper trim board with a small
			// outward projection. Slightly thicker than the trim to
			// catch the eye and read as a separate moulding. Projects
			// along outwardSign so back-wall caps face -z, not +z.
			const cap = addBox(
				dripCapWidth,
				dripCapHeight,
				dripCapDepth,
				trimMatForExtras,
				position.x + dripCenter,
				position.y + height / 2 + trimWidth + dripCapHeight / 2,
				position.z + 0.011 * outwardSign,
				group,
			);
			if (namePrefix) {
				cap.name = `detail/opening-trim/${namePrefix}-dripcap`;
			}
		}
		if (sill) {
			// Sloped sill below the opening — slope sign tracks the
			// outward direction so water always sheds AWAY from the
			// wall. rotation.x = -0.14 tilts the +z edge down (correct
			// for front wall); back wall flips both the projection and
			// the slope sign.
			const sillMesh = addBox(
				sillWidth,
				sillThickness,
				sillDepth,
				sillMatResolved,
				position.x + sillCenter,
				position.y - height / 2 - sillThickness / 2 - 0.001,
				position.z + 0.012 * outwardSign,
				group,
			);
			sillMesh.rotation.x = -0.14 * outwardSign;
			if (namePrefix) {
				sillMesh.name = `detail/opening-trim/${namePrefix}-sill`;
			}
		}
		return;
	}

	if (startLegFits) {
		const near = addBox(
			depth,
			sideHeight,
			trimWidth,
			material,
			position.x,
			sideCenterY,
			position.z + boardStart - trimWidth / 2,
			group,
		);
		if (namePrefix) {
			near.name = `detail/opening-trim/${namePrefix}-near`;
		}
	}
	if (endLegFits) {
		const far = addBox(
			depth,
			sideHeight,
			trimWidth,
			material,
			position.x,
			sideCenterY,
			position.z + boardEnd + trimWidth / 2,
			group,
		);
		if (namePrefix) {
			far.name = `detail/opening-trim/${namePrefix}-far`;
		}
	}
	const top = addBox(
		depth,
		trimWidth,
		boardSpan,
		lyingMaterial,
		position.x,
		position.y + height / 2 + trimWidth / 2,
		position.z + boardCenter,
		group,
	);
	if (namePrefix) {
		top.name = `detail/opening-trim/${namePrefix}-top`;
	}
	if (includeBottom) {
		const bottom = addBox(
			depth,
			trimWidth,
			boardSpan,
			lyingMaterial,
			position.x,
			position.y - height / 2 - trimWidth / 2,
			position.z + boardCenter,
			group,
		);
		if (namePrefix) {
			bottom.name = `detail/opening-trim/${namePrefix}-bottom`;
		}
	}
	if (dripCap) {
		const cap = addBox(
			dripCapDepth,
			dripCapHeight,
			dripCapWidth,
			trimMatForExtras,
			position.x + 0.011 * outwardSign,
			position.y + height / 2 + trimWidth + dripCapHeight / 2,
			position.z + dripCenter,
			group,
		);
		if (namePrefix) {
			cap.name = `detail/opening-trim/${namePrefix}-dripcap`;
		}
	}
	if (sill) {
		const sillMesh = addBox(
			sillDepth,
			sillThickness,
			sillWidth,
			sillMatResolved,
			position.x + 0.012 * outwardSign,
			position.y - height / 2 - sillThickness / 2 - 0.001,
			position.z + sillCenter,
			group,
		);
		// rotation.z = +0.14 tilts the +x edge down (correct for right
		// wall). Left wall flips both the projection and the slope.
		sillMesh.rotation.z = 0.14 * outwardSign;
		if (namePrefix) {
			sillMesh.name = `detail/opening-trim/${namePrefix}-sill`;
		}
	}
}

/** Plan-view heater footprint used to keep benches out of the heater. */
export interface HeaterFootprint {
	minX: number;
	maxX: number;
	minZ: number;
	maxZ: number;
}

/**
 * Clip an X-running bench's span so it stops clear of the heater instead of
 * running straight through it (small two-room sauna sections put the
 * divider-mounted heater inside the bench band). Returns the span unchanged
 * when the bench row doesn't reach the heater, the clipped span when it
 * does, and null when what remains is too short to be a usable bench.
 *
 * **No production caller since issue 131.** The domain's `clipRunToHeater` is
 * now the single clipper for the renderer, the BOM and the clearance check.
 * This survives as the ORACLE that `effectiveRuns.test.ts` checks that clipper
 * against — an independently written implementation of the same rule is worth
 * more as a test fixture than as a second production code path.
 */
export function clipBenchSpanToHeater(
	span: { minX: number; maxX: number },
	benchMinZ: number,
	benchMaxZ: number,
	heater: HeaterFootprint,
	clearanceM = BENCH_HEATER_CLIP_CLEARANCE_MM / 1000,
): { minX: number; maxX: number } | null {
	const zOverlaps =
		benchMinZ < heater.maxZ + clearanceM &&
		benchMaxZ > heater.minZ - clearanceM;
	if (!zOverlaps) return span;
	const xOverlaps =
		span.minX < heater.maxX + clearanceM &&
		span.maxX > heater.minX - clearanceM;
	if (!xOverlaps) return span;

	const heaterCenterX = (heater.minX + heater.maxX) / 2;
	const spanCenterX = (span.minX + span.maxX) / 2;
	const clipped =
		heaterCenterX >= spanCenterX
			? { minX: span.minX, maxX: heater.minX - clearanceM }
			: { minX: heater.maxX + clearanceM, maxX: span.maxX };
	const MIN_BENCH_WIDTH_M = MIN_BENCH_RUN_LENGTH_MM / 1000;
	return clipped.maxX - clipped.minX >= MIN_BENCH_WIDTH_M ? clipped : null;
}

/**
 * Which side of the bench the wall sits against. The wall edge carries the
 * ledger (bärlina) that a wall-hung bench hangs from; the opposite edge is
 * the "front" (room-facing) edge where floor legs land. For orientation
 * "x" the bench runs along X so the wall is on ±Z; for orientation "z" the
 * bench runs along Z so the wall is on ±X.
 */
export type BenchWallSide = "+z" | "-z" | "+x" | "-x";

/**
 * How the bench is held up, matching real Nordic bastulavar:
 *  - "wall": fully wall-hung. Back ledger + perimeter frame + cross joists,
 *    NO floor legs — the bench appears to float (upper/övre lav).
 *  - "wall+frontLegs": back ledger + frame, with vertical posts ONLY at the
 *    room-facing front edge reaching the floor (lower/nedre lav, relax bench).
 *  - "floor": freestanding — perimeter frame with legs at both edges
 *    (near-floor barrel benches).
 *  - "endWalls": a removable frame resting on cleats at BOTH short ends.
 *    The cleats bear against the side walls, not the distant back wall.
 */
export type BenchSupport = "wall" | "wall+frontLegs" | "floor" | "endWalls";

// Real bastu framing lumber. Rails/joists ~45×70 mm; the wall ledger a beefier
// 45×90 mm bärlina; front posts 45×70 mm. Owned by the interior domain (issue
// 130) because the BOM buys these and the clearance check measures them — this
// module used to hold its own copies, and the slat thickness beside them had
// already drifted from what the material list bought.
const BENCH_FRAME_T = BENCH_FRAME_THICKNESS_MM / 1000;
const BENCH_FRAME_H = BENCH_FRAME_HEIGHT_MM / 1000;
const BENCH_LEDGER_H = BENCH_LEDGER_HEIGHT_MM / 1000;
const BENCH_LEG_ALONG = BENCH_LEG_ALONG_MM / 1000;
const BENCH_LEG_ACROSS = BENCH_LEG_ACROSS_MM / 1000;

export function addSlattedBench({
	group,
	width,
	depth,
	height,
	legHeight,
	slatCount,
	slatGapMm,
	material,
	position,
	namePrefix,
	orientation = "x",
	support = "floor",
	wallSide,
	skirt = null,
}: {
	group: THREE.Group;
	width: number;
	depth: number;
	height: number;
	legHeight: number;
	slatCount: number;
	slatGapMm?: number;
	material: THREE.Material;
	position: THREE.Vector3;
	namePrefix?: string;
	orientation?: "x" | "z";
	/** Support structure beneath the slat top. Defaults to freestanding. */
	support?: BenchSupport;
	/**
	 * Side the wall sits on. Required for "wall"/"wall+frontLegs" so the
	 * ledger and front legs land on the correct edges. Ignored for "floor".
	 */
	wallSide?: BenchWallSide;
	/**
	 * Slatted front closing the posts, from `resolveBenchSkirt`. Its heights
	 * are measured from the floor the legs stand on.
	 */
	skirt?: BenchSkirt | null;
}) {
	const safeSlatCount = Math.max(3, slatCount);
	const dimensions = benchFrameDimensions(
		width * 1000,
		depth * 1000,
		support === "endWalls",
	);
	const seatLength = dimensions.seatLengthMm / 1000;
	const gap = slatGapMm === undefined ? Math.min(BENCH_SLAT_GAP_MM / 1000, depth * 0.08) : slatGapMm / 1000;
	const slatDepth = Math.max(
		0.045,
		(depth - gap * (safeSlatCount - 1)) / safeSlatCount,
	);

	const isX = orientation === "x";
	// Work in (u = along-length, v = across-depth) space, then map to world.
	// For "x": u→X, v→Z. For "z": u→Z, v→X.
	const centerU = isX ? position.x : position.z;
	const centerV = isX ? position.z : position.x;

	// Add a board whose footprint is (alongLen × depthLen) centred at (u, v).
	const board = (
		name: string | null,
		alongLen: number,
		boardH: number,
		depthLen: number,
		u: number,
		y: number,
		v: number,
	) => {
		const sizeX = isX ? alongLen : depthLen;
		const sizeZ = isX ? depthLen : alongLen;
		const worldX = isX ? u : v;
		const worldZ = isX ? v : u;
		const mesh = addBox(
			sizeX,
			boardH,
			sizeZ,
			material,
			worldX,
			y,
			worldZ,
			group,
		);
		if (name && namePrefix) {
			mesh.name = name;
		}
		return mesh;
	};

	// Slats: full-length boards spanning the length, tiled across the depth.
	const firstV = centerV - depth / 2 + slatDepth / 2;
	for (let index = 0; index < safeSlatCount; index += 1) {
		board(
			namePrefix ? `detail/bench-slat/${namePrefix}-${index + 1}` : null,
			seatLength,
			height,
			slatDepth,
			centerU,
			position.y,
			firstV + index * (slatDepth + gap),
		);
	}

	// Support frame hangs directly under the slat top.
	const underY = position.y - height / 2;
	const frameY = underY - BENCH_FRAME_H / 2;
	const ledgerY = underY - BENCH_LEDGER_H / 2;

	// wallDir points from the bench centre toward the wall edge (in v).
	const wallDir =
		wallSide === "+z" || wallSide === "+x"
			? 1
			: wallSide === "-z" || wallSide === "-x"
				? -1
				: -1;
	const wallEdgeV = centerV + wallDir * (depth / 2 - BENCH_FRAME_T / 2);
	const frontEdgeV = centerV - wallDir * (depth / 2 - BENCH_FRAME_T / 2);

	const railLength = dimensions.railLengthMm / 1000;

	// Back board: a beefier ledger for wall-mounted benches (the bärlina the
	// bench hangs from), otherwise a plain rail for freestanding benches.
	const wallMounted = support === "wall" || support === "wall+frontLegs";
	if (wallMounted) {
		board(
			namePrefix ? `detail/bench-ledger/${namePrefix}-wall` : null,
			railLength,
			BENCH_LEDGER_H,
			BENCH_FRAME_T,
			centerU,
			ledgerY,
			wallEdgeV,
		);
	} else {
		board(
			namePrefix ? `detail/bench-rail/${namePrefix}-back` : null,
			railLength,
			BENCH_FRAME_H,
			BENCH_FRAME_T,
			centerU,
			frameY,
			wallEdgeV,
		);
	}

	// Front rail (room-facing edge).
	board(
		namePrefix ? `detail/bench-rail/${namePrefix}-front` : null,
		railLength,
		BENCH_FRAME_H,
		BENCH_FRAME_T,
		centerU,
		frameY,
		frontEdgeV,
	);

	// End-supported long rails run over the cleats; the end rails fit between them.
	board(
		namePrefix ? `detail/bench-rail/${namePrefix}-end-a` : null,
		BENCH_FRAME_T,
		BENCH_FRAME_H,
		dimensions.endRailLengthMm / 1000,
		centerU - seatLength / 2 + BENCH_FRAME_T / 2,
		frameY,
		centerV,
	);
	board(
		namePrefix ? `detail/bench-rail/${namePrefix}-end-b` : null,
		BENCH_FRAME_T,
		BENCH_FRAME_H,
		dimensions.endRailLengthMm / 1000,
		centerU + seatLength / 2 - BENCH_FRAME_T / 2,
		frameY,
		centerV,
	);

	// Cross joists span between the front and back rails and carry the slats.
	const joistCount = benchJoistCount(width * 1000);
	for (let j = 0; j < joistCount; j += 1) {
		const frac = (j + 1) / (joistCount + 1);
		board(
			namePrefix ? `detail/bench-rail/${namePrefix}-joist-${j + 1}` : null,
			BENCH_FRAME_T,
			BENCH_FRAME_H,
			dimensions.joistLengthMm / 1000,
			centerU - width / 2 + frac * width,
			frameY,
			centerV,
		);
	}

	// Legs: only where the support mode calls for them.
	if (support === "endWalls") {
		// The full-length front and back rails bear ON these cleats, with
		// the outer faces flush to the two finished wall faces. Installation
		// requires anchoring through the lining into studs/blocking and locking
		// the removable bench frame from below; panel boards alone are no fixing.
		for (const [tag, direction] of [
			["a", -1],
			["b", 1],
		] as const) {
			board(
				namePrefix ? `detail/bench-ledger/${namePrefix}-end-${tag}` : null,
				BENCH_FRAME_T,
				BENCH_LEDGER_H,
				depth,
				centerU + direction * (width / 2 - BENCH_FRAME_T / 2),
				underY - BENCH_FRAME_H - BENCH_LEDGER_H / 2,
				centerV,
			);
		}
		return;
	}
	if (support === "wall") {
		return;
	}

	const legY = underY - legHeight / 2;
	const legCount = benchLegCount(width * 1000);
	const legInset = BENCH_LEG_ALONG / 2 + 0.03;
	// A skirted run's front posts step back behind the skirt boards, which
	// then sit flush under the front rail's face.
	const skirtT = BENCH_SLAT_THICKNESS_MM / 1000;
	const frontLegV = skirt ? frontEdgeV + wallDir * skirtT : frontEdgeV;
	const legEdges: Array<{ v: number; tag: string }> =
		support === "floor"
			? [
					{ v: frontLegV, tag: "front" },
					{ v: wallEdgeV, tag: "back" },
				]
			: [{ v: frontLegV, tag: "front" }];

	for (const edge of legEdges) {
		for (let i = 0; i < legCount; i += 1) {
			const frac = legCount === 1 ? 0.5 : i / (legCount - 1);
			const u = centerU - width / 2 + legInset + frac * (width - 2 * legInset);
			board(
				namePrefix
					? `detail/bench-leg/${namePrefix}-${edge.tag}-${i + 1}`
					: null,
				BENCH_LEG_ALONG,
				legHeight,
				BENCH_LEG_ACROSS,
				u,
				legY,
				edge.v,
			);
		}
	}

	if (!skirt) return;
	const floorY = underY - legHeight;
	const boardH = BENCH_SLAT_WIDTH_MM / 1000;
	const pitch = boardH + BENCH_SLAT_GAP_MM / 1000;
	// Outer face flush with the seat's front edge, like the rail above it.
	const frontFaceV = centerV - wallDir * (depth / 2 - skirtT / 2);
	const endLength = skirt.endLengthMm / 1000;
	// End boards run from behind the front skirt back toward the wall — past
	// the run's own back edge when it stands out from the wall.
	const endCenterV =
		centerV - (wallDir * depth) / 2 + wallDir * (skirtT + endLength / 2);
	for (let i = 0; i < skirt.boardCount; i += 1) {
		const y = floorY + skirt.topMm / 1000 - boardH / 2 - i * pitch;
		board(
			namePrefix ? `detail/bench-skirt/${namePrefix}-front-${i + 1}` : null,
			skirt.frontLengthMm / 1000,
			boardH,
			skirtT,
			centerU,
			y,
			frontFaceV,
		);
		for (const [tag, open, direction] of [
			["a", skirt.openAtStart, -1],
			["b", skirt.openAtEnd, 1],
		] as const) {
			if (!open) continue;
			board(
				namePrefix
					? `detail/bench-skirt/${namePrefix}-end-${tag}-${i + 1}`
					: null,
				skirtT,
				boardH,
				endLength,
				centerU + direction * (seatLength / 2 - skirtT / 2),
				y,
				endCenterV,
			);
		}
	}
}
